import React from 'react'
import { Dropdown, Progress, Spin, Button } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import FTIcon from 'components/FileTypeIcon'

import i18n from 'i18n'
import classNames from 'classnames'

import { ClientJS } from 'clientjs'
import { formatBytes } from 'utils'
import { errorBase } from 'utils/ui'
import { FILE_ACCESS_LEVEL } from 'types/utils'
import { getCachedFile, setCachedFile } from 'utils/attachmentCache'
import { serveAttachmentForPreview } from 'utils/attachmentPreviewTransfer'

import downIcon from '@/assets/images/chevron-down-gray2-24.svg'
import closeIcon from '@/assets/images/icon-clear-20.svg'
import helper from './helper'

import styles from './style.module.scss'

class Attachment extends React.Component {
  state = {
    percent: -1,
    isEnabled: true,
    thumbLoading: false,
    loading: false,
  }

  antIcon = (<LoadingOutlined size="small" spin />)

  async componentDidMount() {
    const { attachmentObj } = this.props
    const { file, startEncoding } = attachmentObj
    const { name, type } = file

    if (name === null && type === 'message/rfc822') {
      this.setState({
        thumbLoading: true,
      })
      console.log(' --- file: ', file)
      this.preDownload()
    }

    if (startEncoding != null) {
      await startEncoding(this)
    }
  }

  componentWillUnmount() {}

  onProgress = (evt, percent) => {
    console.log('onProgress - ', evt, percent)

    this.setState({
      percent,
    })

    if (percent >= 100) {
      setTimeout(() => {
        this.setState({
          percent: -1,
          isEnabled: true,
        })
      }, 500)
    }
  }

  setLoading = (loading) => {
    this.setState({
      loading,
    })
  }

  setFileElem = (ref) => {
    if (ref != null) {
      this.ref = ref
      // console.log(' ---- ref ---- ', ref, ref.innerText)
      const elems = ref.querySelectorAll('span')
      if (elems.length > 0) {
        requestAnimationFrame(() => {
          // const w = elems[1].offsetWidth // + 1
          let w = elems[1].offsetWidth // + 1;
          const wStr = window.getComputedStyle(elems[1], null).width
          if (wStr.substring(wStr.length - 2) === 'px') {
            const num = wStr.substring(0, wStr.length - 2)
            w = window.parseFloat(num)
          }
          if (ref.offsetWidth > w) {
            elems[1].style.maxWidth = `${w}px`
            elems[0].style.maxWidth = `calc(100% - ${w}px)`
          }
        })
      }
    }
  }

  deleteAttachment = (e) => {
    const { enableDelete, attachmentObj } = this.props
    // console.log(' delete attachment')

    if (enableDelete !== false) {
      attachmentObj.delete()
    }

    if (e != null) {
      e.stopPropagation()
    }
  }

  download = (onDownload, options) => {
    const { attachmentObj } = this.props

    attachmentObj
      .download(this.onProgress, this, options)
      .then((ret) => {
        console.log('download - ', ret)
        if (onDownload != null) {
          onDownload(ret)
        }
      })
      .catch((e) => {
        console.error('download - ', e)
        // errorBase({
        //   title: 'Error',
        //   content: e.message,
        // })
        if (onDownload != null) {
          onDownload(null)
        }
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  preDownload = () => {
    this.download((ret) => {
      this.downloadObj = ret

      const { attachmentObj } = this.props
      const { file } = attachmentObj

      const newFile = {
        ...file,
      }

      if (ret.newFileName != null) {
        newFile.name = ret.newFileName
      }

      this.setState({
        file: newFile,
        thumbLoading: false,
      })
    })
  }

  downloadBackground = (options) => {
    const { attachmentObj } = this.props
    attachmentObj.download(() => {}, { setLoading: () => {} }, options).catch(() => {})
  }

  downloadFile = async () => {
    const { attachmentObj } = this.props
    const { file } = attachmentObj

    const triggerDownload = (blobUrl) => {
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = file.name
      link.click()
    }

    // 1. 인메모리 캐시
    if (this.downloadObj?.saveFile != null) {
      this.downloadObj.saveFile()
      this.downloadBackground()
      return
    }
    if (this.downloadObj?.downloadPath != null) {
      triggerDownload(this.downloadObj.downloadPath)
      this.downloadBackground()
      return
    }

    // 2. IndexedDB 캐시
    const cacheKey = attachmentObj.url || `${file.name}_${file.size}`
    const cachedBlob = await getCachedFile(cacheKey)
    if (cachedBlob != null) {
      const blobUrl = URL.createObjectURL(cachedBlob)
      this.downloadObj = { downloadPath: blobUrl }
      triggerDownload(blobUrl)
      this.downloadBackground()
      return
    }

    // 3. 서버에서 다운로드 후 IndexedDB에 저장
    this.download((ret) => {
      if (ret != null && ret.saveFile != null) {
        ret.saveFile()
        fetch(ret.downloadPath)
          .then((r) => r.blob())
          .then((blob) => setCachedFile(cacheKey, blob))
          .catch(() => {})
      }
    })
  }

  showPreview = () => {
    const { attachmentObj } = this.props
    const { file } = attachmentObj
    const { type } = file
    if (type != null) {
      const mimetype = type.toLowerCase()

      if (mimetype != null) {
        if (mimetype.indexOf('image') === 0) {
          attachmentObj.showImage()
        } else if (mimetype.indexOf('video') === 0 || mimetype.indexOf('audio') === 0) {
          attachmentObj.showVideo()
        } else if (mimetype.indexOf('text/') === 0) {
          attachmentObj.showText()
        } else if (mimetype === 'message/rfc822') {
          attachmentObj.showEML()
        }
      }
    }
  }

  handleRestrictedClick = () => {
    const { headerAccessLevel } = this.props
    if (headerAccessLevel === FILE_ACCESS_LEVEL.NO_ACCESS) {
      errorBase({
        title: i18n.t('record.attachment.noAccessTitle'),
        content: i18n.t('record.attachment.noAccessContent'),
        width: 480,
        onButtonProps: {
          className: classNames('type7'),
        },
      })
    }
  }

  openInNewTab = async () => {
    const { attachmentObj } = this.props
    const { file } = attachmentObj

    const client = new ClientJS()
    const isSafari = client.isSafari()
    // Safari는 async 이후 window.open이 차단되므로 가장 먼저 실행
    const viewerWindow = isSafari ? window.open('', '_blank') : null

    const typeParam = helper.isTextViewable(file) ? '&type=text' : ''

    // /viewer 는 cross-origin isolation 되어 opener 의 blob: URL 을 못 읽는다.
    // Blob 을 BroadcastChannel 로 전달(transferId)하고, 미지원 시에만 레거시
    // blob: URL 을 넘긴다.
    const openViewer = (blob, blobUrl) => {
      const transferId = serveAttachmentForPreview(blob, {
        name: file.name,
        mimeType: file.type,
      })
      const viewerUrl =
        transferId != null
          ? `/viewer?ch=${encodeURIComponent(transferId)}&name=${encodeURIComponent(file.name)}${typeParam}`
          : `/viewer?url=${encodeURIComponent(blobUrl)}&name=${encodeURIComponent(file.name)}${typeParam}`
      if (viewerWindow) {
        viewerWindow.location.href = viewerUrl
      } else {
        window.open(viewerUrl, '_blank')
      }
      this.setLoading(false)
    }

    // blob: URL 로부터 자기 탭에서 Blob 을 확보 (opener 는 자기 blob 접근 가능)
    const blobFromUrl = async (blobUrl) => {
      try {
        return await fetch(blobUrl).then((r) => r.blob())
      } catch {
        return null
      }
    }

    const cacheKey = attachmentObj.url || `${file.name}_${file.size}`

    // 1. 인메모리 캐시 (새로고침 전)
    if (this.downloadObj?.downloadPath != null) {
      const blobUrl = this.downloadObj.downloadPath
      openViewer(await blobFromUrl(blobUrl), blobUrl)
      this.downloadBackground({ extraParams: 'purpose=preview' })
      return
    }

    // 2. IndexedDB 캐시 (새로고침 후, 1시간 이내)
    const cachedBlob = await getCachedFile(cacheKey)
    if (cachedBlob != null) {
      const blobUrl = URL.createObjectURL(cachedBlob)
      this.downloadObj = { downloadPath: blobUrl }
      openViewer(cachedBlob, blobUrl)
      this.downloadBackground({ extraParams: 'purpose=preview' })
      return
    }

    // 3. 서버에서 다운로드
    this.download(
      async (ret) => {
        if (ret == null) {
          if (viewerWindow) viewerWindow.close()
          errorBase({
            title: 'Error',
            content: 'Failed to load file data',
          })
          return
        }

        this.downloadObj = ret
        const blob = await blobFromUrl(ret.downloadPath)
        openViewer(blob, ret.downloadPath)

        // 백그라운드에서 IndexedDB에 저장 (이미 확보한 blob 재사용)
        if (blob != null) setCachedFile(cacheKey, blob).catch(() => {})
      },
      { extraParams: 'purpose=preview' },
    )
  }

  fetchByXhr = (url) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.responseType = 'blob'
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 400) resolve(xhr.response)
        else reject(new Error(`HTTP ${xhr.status}`))
      })
      xhr.addEventListener('error', () => reject(new Error('Network error')))
      xhr.open('GET', url, true)
      xhr.send()
    })
  }

  runSingleTest = async (method, url, file, decode) => {
    const dlStart = performance.now()
    const blob =
      method === 'xhr'
        ? await this.fetchByXhr(url)
        : await fetch(url).then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`)
            return r.blob()
          })
    const dlEnd = performance.now()

    const tmpFile = new File([blob], file.name, { type: file.type })
    await decode(tmpFile)
    const decEnd = performance.now()

    return {
      download: dlEnd - dlStart,
      decrypt: decEnd - dlEnd,
      total: decEnd - dlStart,
    }
  }

  // compareDownload = async () => {
  //   const { attachmentObj } = this.props
  //   const { file, resourceData, url: currentUrl } = attachmentObj
  //   const presignUrl = resourceData?.presign_url

  //   const label = `"${file.name}" (${formatBytes(file.size, 0, true)})`
  //   console.log(`\n[Test] === 다운로드 속도 비교: ${label} (3회 반복, 랜덤 순서) ===`)
  //   console.log('[Test] 백엔드 URL:', currentUrl)
  //   console.log('[Test] S3 presign_url:', presignUrl)

  //   if (!presignUrl) {
  //     console.warn('[Test] presign_url 없음 — S3 비교 불가')
  //     return
  //   }

  //   const ROUNDS = 3
  //   const results = { backend: [], s3: [] }

  //   // 6회 실행: backend 3회 + s3 3회를 랜덤 순서로 섞기
  //   const schedule = []
  //   for (let i = 0; i < ROUNDS; i += 1) {
  //     schedule.push({ type: 'backend', idx: i })
  //     schedule.push({ type: 's3', idx: i })
  //   }
  //   for (let i = schedule.length - 1; i > 0; i -= 1) {
  //     const j = Math.floor(Math.random() * (i + 1))
  //     const tmp = schedule[i]
  //     schedule[i] = schedule[j]
  //     schedule[j] = tmp
  //   }

  //   console.log('[Test] 실행 순서:', schedule.map((s) => s.type).join(' → '))

  //   for (let i = 0; i < schedule.length; i += 1) {
  //     const { type, idx } = schedule[i]
  //     try {
  //       if (type === 'backend') {
  //         // eslint-disable-next-line no-await-in-loop
  //         const r = await this.runSingleTest('xhr', currentUrl, file, attachmentObj.decode)
  //         results.backend.push(r)
  //         console.log(
  //           `[Test] 백엔드 #${idx + 1} — DL: ${r.download.toFixed(0)}ms, 복호화: ${r.decrypt.toFixed(0)}ms, 총: ${r.total.toFixed(0)}ms`,
  //         )
  //       } else {
  //         // eslint-disable-next-line no-await-in-loop
  //         const r = await this.runSingleTest('fetch', presignUrl, file, attachmentObj.decode)
  //         results.s3.push(r)
  //         console.log(
  //           `[Test] S3     #${idx + 1} — DL: ${r.download.toFixed(0)}ms, 복호화: ${r.decrypt.toFixed(0)}ms, 총: ${r.total.toFixed(0)}ms`,
  //         )
  //       }
  //     } catch (e) {
  //       console.error(`[Test] ${type} #${idx + 1} 실패:`, e)
  //     }
  //   }

  //   const avg = (arr, key) => arr.reduce((sum, r) => sum + r[key], 0) / arr.length

  //   if (results.backend.length > 0 && results.s3.length > 0) {
  //     const backendAvg = avg(results.backend, 'total')
  //     const s3Avg = avg(results.s3, 'total')
  //     const diff = Math.abs(backendAvg - s3Avg)
  //     const faster = backendAvg < s3Avg ? '백엔드' : 'S3'

  //     console.log(`\n[Test] ── 평균 결과 (${ROUNDS}회) ──`)
  //     console.log(
  //       `[Test] 백엔드 평균 — DL: ${avg(results.backend, 'download').toFixed(0)}ms, 복호화: ${avg(results.backend, 'decrypt').toFixed(0)}ms, 총: ${backendAvg.toFixed(0)}ms`,
  //     )
  //     console.log(
  //       `[Test] S3     평균 — DL: ${avg(results.s3, 'download').toFixed(0)}ms, 복호화: ${avg(results.s3, 'decrypt').toFixed(0)}ms, 총: ${s3Avg.toFixed(0)}ms`,
  //     )
  //     console.log(
  //       `[Test] 결과: ${faster}가 ${diff.toFixed(0)}ms 더 빠름 (${((diff / Math.max(backendAvg, s3Avg)) * 100).toFixed(1)}% 차이)`,
  //     )
  //   }
  //   console.log(`[Test] === 비교 완료 ===\n`)
  // }

  getDropDownAttrs = () => {
    const { file } = this.state
    const { enablePreview, downloadable, headerAccessLevel } = this.props

    const items = []

    if (headerAccessLevel === FILE_ACCESS_LEVEL.NO_ACCESS) {
      return null
    }

    const isPreviewAllowed = headerAccessLevel !== FILE_ACCESS_LEVEL.NO_ACCESS
    const isDownloadAllowed = headerAccessLevel !== FILE_ACCESS_LEVEL.PREVIEW_ONLY

    if (enablePreview !== false && helper.isPreviewEnable(file) === true) {
      items.push({
        label: i18n.t('record.menu.preview'),
        key: 'preview',
      })
    }

    const {
      attachmentObj: { file: attachmentFile },
    } = this.props
    if (helper.isViewEnabled(attachmentFile) && isPreviewAllowed) {
      items.push({
        key: 'view',
        label: i18n.t('record.menu.preview'),
      })
    }

    if (downloadable !== false && isDownloadAllowed) {
      items.push({
        label: i18n.t('common.title.download'),
        key: 'download',
      })
    }

    if (items.length === 0) {
      return null
    }

    const menu = {
      items,
      onClick: (e) => {
        console.log(' --- onMenuClick : ', e.key)

        if (e.key === 'download') {
          this.downloadFile()
        } else if (e.key === 'delete') {
          this.deleteAttachment()
        } else if (e.key === 'preview') {
          this.showPreview()
        } else if (e.key === 'view') {
          this.openInNewTab()
        }
        // else if (e.key === 'test') {
        //   this.compareDownload()
        // }
      },
      // selectable: true,
      // selectedKeys: [searchType],
    }

    const dropdownAttrs = {
      overlayClassName: classNames('contextMenu', styles.dropdown),
      menu,
      trigger: ['click'],
      arrow: false,
      placement: 'bottomRight',
      // destroyPopupOnHide: false,
      align: {
        offset: [0, 0],
      },
    }

    return dropdownAttrs
  }

  render() {
    const { percent, isEnabled, thumbLoading, loading } = this.state
    const { attachmentObj, enableDelete, forEditor } = this.props
    const { file } = attachmentObj

    const { name, size, type } = file

    const progressElem =
      percent >= 0 && percent <= 100 ? (
        <Progress className={styles.progress} percent={percent} showInfo={false} size={2} />
      ) : (
        ''
      )

    const thumbImg = (
      <div className={styles.fileIcon}>
        <FTIcon colorType="multiColor" fileName={name} />
      </div>
    )

    const wrapperClsMap = {}
    const clsMap = {}

    clsMap[styles.icon] = true
    wrapperClsMap.iconItem = true
    clsMap[type] = true
    clsMap[styles.isProgressing] = percent >= 0 && percent <= 100
    clsMap.forEditor = forEditor === true

    const isClosable = isEnabled && enableDelete !== false

    clsMap[styles.closable] = isClosable

    const nameTxt = name || ''

    // 확장자가 표시되게 하거나, 감추는 걸 여기서 결정.
    let pos = -1 // nameTxt.lastIndexOf('.')

    if (pos < 0) {
      pos = nameTxt.length
    }

    const front = nameTxt.substring(0, pos)
    const end = nameTxt.substring(pos)

    const filenameElem = (
      <div
        ref={(ref) => {
          this.setFileElem(ref)
        }}
        className={classNames(styles.filename)}
      >
        <span className={styles.front}>{front}</span>
        <span className={styles.end}>{end}</span>
      </div>
    )

    const fileExt = nameTxt.substring(nameTxt.lastIndexOf('.') + 1, nameTxt.length)
    const dropdownAttrs = this.getDropDownAttrs()

    const { headerAccessLevel } = this.props
    const isNoAccess = enableDelete === false && headerAccessLevel === FILE_ACCESS_LEVEL.NO_ACCESS
    const isDropdown = dropdownAttrs != null && enableDelete === false
    const isViewOnlyAndNotDragDocs =
      helper.isNotDragDocsExtension(file) && headerAccessLevel === FILE_ACCESS_LEVEL.PREVIEW_ONLY

    let menuElem = null
    if (isNoAccess || isViewOnlyAndNotDragDocs) {
      menuElem = null
    } else if (isDropdown) {
      menuElem = (
        <div className={styles.menu} style={{ cursor: 'pointer' }}>
          <img src={downIcon} alt="menu" />
        </div>
      )
    } else {
      menuElem = (
        <Button
          icon={<img src={closeIcon} alt="menu" />}
          onClick={this.deleteAttachment}
          className={styles.closeIcon}
        />
      )
    }

    const content = (
      <div className={styles.fileInfo} style={{ cursor: 'pointer' }}>
        <div role="button" tabIndex="-1" onKeyDown={() => {}}>
          {filenameElem}
          <div className={styles.line2}>
            <span className={styles.fileExt}>{fileExt}</span>
            <span className={styles.fileSize}>{formatBytes(size, 0, true)}</span>
          </div>
          {thumbImg}
        </div>
        {menuElem}
      </div>
    )

    const showLoading = loading === true || thumbLoading === true
    const isRestricted = isNoAccess || isViewOnlyAndNotDragDocs

    let attachmentItem
    if (isRestricted) {
      attachmentItem = (
        <div
          role="button"
          tabIndex={0}
          className={classNames(styles.attachmentItem, 'attachmentItem', clsMap)}
          onClick={this.handleRestrictedClick}
          onKeyDown={(e) => e.key === 'Enter' && this.handleRestrictedClick()}
        >
          <Spin indicator={this.antIcon} spinning={showLoading}>
            {content}
          </Spin>
          {progressElem}
        </div>
      )
    } else {
      attachmentItem = (
        <div className={classNames(styles.attachmentItem, 'attachmentItem', clsMap)}>
          <Spin indicator={this.antIcon} spinning={showLoading}>
            {content}
          </Spin>
          {progressElem}
        </div>
      )
    }

    return (
      <div className={styles.attachmentBox}>
        {isDropdown ? (
          <Dropdown {...dropdownAttrs}>
            <div className={classNames(styles.attachmentItem, 'attachmentItem', clsMap)}>
              <Spin indicator={this.antIcon} spinning={showLoading}>
                {content}
              </Spin>
              {progressElem}
            </div>
          </Dropdown>
        ) : (
          attachmentItem
        )}
      </div>
    )
  }
}

export default Attachment
